import { calculateCockpitGrowth, calculateCockpitSobGrowth } from '../calculateCockpitGrowth'
import { IRevenueAndSobCategoryRow } from '../revenueAndSob'

import { getPlanningQuality } from './getPlanningQuality'
import { IPlanQualityCategoryRow, IPlanQualityMetrics } from './types'
import { weightedAverage } from './weightedAverage'

const mapToPlanQualityRow = ({
  category,
  isTotal,
  sales,
  sob,
}: IRevenueAndSobCategoryRow): IPlanQualityCategoryRow => {
  const financialYoy = calculateCockpitGrowth({ value: sales.plan, baseline: sales.baseline })
  const sobYoy = calculateCockpitSobGrowth({ value: sob.plan, baseline: sob.baseline })

  return {
    category,
    isTotal,
    financial: {
      baseline: sales.baseline,
      plan: sales.plan,
      yoy: financialYoy,
      marketTarget: sales.marketGrowth,
      score: getPlanningQuality(financialYoy, sales.marketGrowth),
      quota: sales.quota,
    },
    sob: {
      baseline: sob.baseline,
      plan: sob.plan,
      yoy: sobYoy,
      marketTarget: sob.marketGrowth,
      score: getPlanningQuality(sobYoy, sob.marketGrowth),
      quota: sob.quota,
    },
  }
}

/** The Total row's market benchmark, rolled up from its categories and baseline-weighted, and the
 *  score that follows from it.
 *
 *  The aggregate has no published benchmark of its own: the API attaches `finYoyGrowth`/
 *  `sobYoyGrowth` to a category's rows, and the offering-total rows the Total row is built from
 *  carry none — so scoring it against its own `marketTarget` leaves it `null`, and a `null` pillar
 *  silently drops out of the badge's average.
 *
 *  Rolled up unconditionally rather than only when the row's own target is missing, because
 *  `getPlanQualityHierarchyRows` does exactly that for every node above the category level — see
 *  its `isOwnTargetTrustworthy`. Reading the published rate here the moment the backend starts
 *  filling it would put the badge back at odds with the table row it sits above.
 *
 *  Both pillars weight by the financial baseline, as the hierarchy does: SOB is a share, with no
 *  money of its own to weight by. */
const withRolledUpTotalTarget = (
  row: IPlanQualityCategoryRow,
  categoryRows: IPlanQualityCategoryRow[]
): IPlanQualityCategoryRow => {
  if (!row.isTotal) {
    return row
  }

  const rollUpTarget = (pillar: 'financial' | 'sob') =>
    weightedAverage(
      categoryRows
        .filter((categoryRow) => !categoryRow.isTotal)
        .map((categoryRow) => ({
          value: categoryRow[pillar].marketTarget,
          weight: categoryRow.financial.baseline,
        }))
    )

  const withTarget = (metrics: IPlanQualityMetrics, pillar: 'financial' | 'sob') => {
    const marketTarget = rollUpTarget(pillar)

    return { ...metrics, marketTarget, score: getPlanningQuality(metrics.yoy, marketTarget) }
  }

  return {
    ...row,
    financial: withTarget(row.financial, 'financial'),
    sob: withTarget(row.sob, 'sob'),
  }
}

/** Per-category rows for the Plan Quality table, derived from the same category breakdown the
 *  Revenue & SOB table renders — including the `OVERALL_CATEGORY` total, same as
 *  `getRevenueAndSobByCategory` returns it, so the table can render it as its own bolded row.
 *
 *  Every row, the Total included, scores its own YoY against a market target — the same rule
 *  `getPlanQualityHierarchyRows` applies to the tree, down to where that target comes from, so the
 *  badge built from the Total row cannot contradict the table beneath it. The Achievement
 *  pipeline's `getAchievementScoreRows` is the twin of this, and states the case against averaging
 *  the categories' scores instead. */
export const getPlanQualityRows = (
  rows: IRevenueAndSobCategoryRow[]
): IPlanQualityCategoryRow[] => {
  const categoryRows = rows.map(mapToPlanQualityRow)

  return categoryRows.map((row) => withRolledUpTotalTarget(row, categoryRows))
}
